import { useMemo, useState, type FormEvent } from 'react';
import { Badge } from '../../../atoms/Badge/Badge';
import { Button } from '../../../atoms/Button/Button';
import { Checkbox } from '../../../atoms/Checkbox/Checkbox';
import { Icon } from '../../../atoms/Icon/Icon';
import { Toggle } from '../../../atoms/Toggle/Toggle';
import { InputField } from '../../../molecules/InputField/InputField';
import { Modal } from '../../../molecules/Modal/Modal';
import { Search } from '../../../molecules/Search/Search';
import { Select } from '../../../molecules/Select/Select';
import { Tabs } from '../../../molecules/Tabs/Tabs';
import { TableHeader } from '../../../molecules/TableHeader/TableHeader';
import { TextareaField } from '../../../molecules/TextareaField/TextareaField';
import type { TableHeaderSort } from '../../../molecules/TableHeaderLabel/TableHeaderLabel';
import { Table, type TableColumn, type TableRowData } from '../../../organisms/Table/Table';
import styles from './ReportsWorkspace.module.css';

export type ReportListItem = {
  id: string;
  name: string;
  author: string;
  created: string;
  modified: string;
  widgets: number;
};
export type ReportSortKey = 'name' | 'author' | 'created' | 'modified' | 'widgets';
export type ReportSort = { key: ReportSortKey; direction: 'asc' | 'desc' } | null;

export function ReportsTable({
  reports,
  total,
  query,
  sort,
  onQueryChange,
  onSort,
  onView,
  onDelete,
}: {
  reports: ReportListItem[];
  total: number;
  query: string;
  sort: ReportSort;
  onQueryChange: (value: string) => void;
  onSort: (key: ReportSortKey) => void;
  onView: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const sortFor = (key: ReportSortKey): TableHeaderSort =>
    sort?.key === key ? sort.direction : 'selector';
  const sortable = (key: ReportSortKey, header: string, width: string): TableColumn => ({
    key,
    header,
    width,
    sort: sortFor(key),
    onSort: () => onSort(key),
  });
  const columns: TableColumn[] = [
    sortable('name', 'Name', '21.5%'),
    { ...sortable('author', 'Created by', '21%'), cellType: 'badge' },
    sortable('created', 'Date created', '20.5%'),
    sortable('modified', 'Last modified', '20.5%'),
    sortable('widgets', 'Widgets', '7.5%'),
    { key: 'actions', header: 'Actions', cellType: 'actionButtons', width: '9%' },
  ];
  const rows: TableRowData[] = reports.map((report) => ({
    key: report.id,
    cells: {
      name: { text: report.name },
      author: { content: <Badge type="gray">{report.author}</Badge> },
      created: { text: report.created },
      modified: { text: report.modified },
      widgets: { text: String(report.widgets) },
      actions: {
        content: (
          <span className={styles.rowActions}>
            <Button
              hierarchy="ghostColor"
              iconTrailing={<Icon name="chevronRight" />}
              onClick={() => onView(report.id)}
            >
              View
            </Button>
            <Button
              hierarchy="ghostGrey"
              iconLeading={<Icon name="deleteBin" />}
              aria-label={`Delete ${report.name}`}
              onClick={() => onDelete(report.id)}
            />
          </span>
        ),
      },
    },
  }));
  return (
    <Table
      label="Reports"
      type={total === 0 ? 'empty' : 'default'}
      columns={columns}
      rows={rows}
      emptyLabel={total === 0 ? 'No reports created yet' : `No reports match “${query.trim()}”.`}
      header={
        <TableHeader
          title="Reports"
          badge={`${total} report${total === 1 ? '' : 's'}`}
          helpText="Saved reports in this workspace, showing their creator, creation date, last update and widget count. Open a report to view or arrange its widgets."
          search={
            <Search
              className={styles.search}
              value={query}
              onValueChange={onQueryChange}
              placeholder="Search reports..."
              aria-label="Search reports"
            />
          }
        />
      }
    />
  );
}

export function CreateReportModal({
  open,
  suggestedName,
  pending,
  onClose,
  onCreate,
}: {
  open: boolean;
  suggestedName: string;
  pending?: boolean;
  onClose: () => void;
  onCreate: (name: string) => void;
}) {
  const [name, setName] = useState(suggestedName);
  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (name.trim()) onCreate(name.trim());
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create a report"
      description="Give it a name. It starts with summary widgets you can add to, remove and rearrange."
      icon={<Icon name="customReport" />}
      confirmLabel={pending ? 'Creating…' : 'Continue'}
      confirmDisabled={!name.trim() || pending}
      onConfirm={() => submit()}
      cancelLabel="Cancel"
    >
      <form className={styles.form} onSubmit={submit}>
        <InputField label="Report name" required value={name} onValueChange={setName} />
      </form>
    </Modal>
  );
}

/**
 * `present` is "this widget is already on the report". Figma (2069:11637) draws
 * the picker with only checked and unchecked boxes — there is no disabled
 * variant in it — so a widget already on the report shows as *checked* and can
 * be unchecked to take it off again, rather than being locked out.
 */
export type WidgetChoice = {
  id: string;
  label: string;
  /** Searched against, not drawn — Figma's list (2069:11637) is label-only. */
  module: string;
  present?: boolean;
};
export function AddWidgetModal({
  open,
  choices,
  pending,
  onClose,
  onConfirm,
}: {
  open: boolean;
  choices: WidgetChoice[];
  pending?: boolean;
  onClose: () => void;
  /** The widgets the report should end up with — not just the newly ticked ones. */
  onConfirm: (ids: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const present = useMemo(
    () => choices.filter((choice) => choice.present).map((choice) => choice.id),
    [choices],
  );
  const [selected, setSelected] = useState<string[]>(() => present);
  const added = selected.filter((id) => !present.includes(id));
  const removed = present.filter((id) => !selected.includes(id));
  const shown = useMemo(
    () =>
      choices.filter((choice) =>
        `${choice.module} ${choice.label}`.toLowerCase().includes(query.toLowerCase()),
      ),
    [choices, query],
  );
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a widget"
      description="Choose widgets from any dashboard module."
      icon={<Icon name="add" />}
      confirmLabel={pending ? 'Saving…' : 'Save changes'}
      confirmDisabled={(added.length === 0 && removed.length === 0) || pending}
      onConfirm={() => onConfirm(selected)}
      cancelLabel="Cancel"
    >
      <div className={styles.widgetPicker}>
        <Search
          value={query}
          onValueChange={setQuery}
          placeholder="Search widgets..."
          aria-label="Search widgets"
        />
        <div className={styles.widgetList}>
          {shown.map((choice) => (
            <Checkbox
              key={choice.id}
              label={choice.label}
              checked={selected.includes(choice.id)}
              onChange={(checked) =>
                setSelected((current) =>
                  checked ? [...current, choice.id] : current.filter((id) => id !== choice.id),
                )
              }
            />
          ))}
          {shown.length === 0 && <p className={styles.empty}>No widgets match your search.</p>}
        </div>
      </div>
    </Modal>
  );
}

export type CampaignPostRow = {
  id: number;
  summary: string;
  keyword: string;
  engagement: string;
  url: string;
};
export type CampaignDraft = {
  keywords: string[];
  officialOnly: boolean;
  postIds: number[];
  urls?: string[];
};

/**
 * Figma `Configure campaigns` — four frames of one modal:
 *
 *   `Add by keywords` (2068:29797)  tabs + keyword tags + the official-only
 *                                   toggle, primary `Search`
 *   `Add manually`    (2161:35088)  tabs + one URL per line, primary `Set`
 *   results           (2068:31496)  the detected posts, primary `Save`
 *   configured        (2216:117524) the same table read-only, primary `Edit`
 *
 * So `step` and `tab` are the two pieces of state the frames differ by, and a
 * report that already carries a campaign opens on the fourth.
 *
 * `Bulk upload URLs`, drawn under the manual tab, is deliberately not here —
 * left out rather than drawn dead, so nothing in the UI offers a capability
 * that does not exist.
 */
export function CampaignModal({
  open,
  initialKeywords,
  initialOfficialOnly,
  initialPostIds,
  initialUrls,
  posts,
  searching,
  pending,
  onSearch,
  onClose,
  onSave,
}: {
  open: boolean;
  initialKeywords: string[];
  initialOfficialOnly: boolean;
  initialPostIds: number[];
  initialUrls: string[];
  /** The last search's results, already formatted. */
  posts: CampaignPostRow[];
  searching?: boolean;
  pending?: boolean;
  onSearch: (keywords: string[], officialOnly: boolean) => void;
  onClose: () => void;
  onSave: (campaign: CampaignDraft) => void;
}) {
  const configured = initialPostIds.length > 0;
  const [step, setStep] = useState<'input' | 'results'>(configured ? 'results' : 'input');
  const [tab, setTab] = useState<'keywords' | 'manual'>('keywords');
  const [keywords, setKeywords] = useState(initialKeywords);
  const [draft, setDraft] = useState('');
  const [urls, setUrls] = useState(initialUrls.join('\n'));
  const [officialOnly, setOfficialOnly] = useState(initialOfficialOnly);
  /*
   * Held as exclusions, not selections: a search's results all belong to the
   * campaign — the frames go from "10 posts detected" straight to "10 posts
   * configured" — and the boxes are there to drop the odd false positive. An
   * inclusion list would have to be filled in once the results land, which is
   * state chasing async data; this needs nothing when they arrive.
   */
  const [dropped, setDropped] = useState<number[]>([]);
  /*
   * The fourth frame is the saved campaign, read back. It stops being read-only
   * the moment `Edit` sends the user back to the keywords, and does not become
   * read-only again until the next open.
   */
  const [readOnly, setReadOnly] = useState(configured);

  const shown = readOnly ? posts.filter((post) => initialPostIds.includes(post.id)) : posts;
  const kept = shown.filter((post) => !dropped.includes(post.id));
  const allKept = shown.length > 0 && dropped.length === 0;

  const search = () => {
    onSearch(keywords, officialOnly);
    setStep('results');
  };
  const toResults = step === 'results';

  const columns: TableColumn[] = [
    ...(readOnly
      ? []
      : [
          {
            key: 'select',
            width: '5%',
            headerCheckbox: (
              <Checkbox
                aria-label="Select every detected post"
                checked={allKept}
                indeterminate={!allKept && kept.length > 0}
                onChange={(on) => setDropped(on ? [] : shown.map((post) => post.id))}
              />
            ),
          } satisfies TableColumn,
        ]),
    { key: 'summary', header: 'Post summary' },
    { key: 'keyword', header: 'Keyword', cellType: 'badge', width: '12%' },
    { key: 'engagement', header: 'Engagement', width: '13%' },
    { key: 'view', cellType: 'actionButtons', width: '10%' },
  ];
  const rows: TableRowData[] = shown.map((post) => ({
    key: String(post.id),
    cells: {
      ...(readOnly
        ? {}
        : {
            select: {
              content: (
                <Checkbox
                  aria-label={`Keep “${post.summary}”`}
                  checked={!dropped.includes(post.id)}
                  onChange={(on) =>
                    setDropped((current) =>
                      on ? current.filter((id) => id !== post.id) : [...current, post.id],
                    )
                  }
                />
              ),
            },
          }),
      summary: { text: post.summary },
      keyword: { content: <Badge type="gray">{post.keyword}</Badge> },
      engagement: { text: post.engagement },
      view: {
        content: (
          <Button hierarchy="ghostColor" href={post.url}>
            View
          </Button>
        ),
      },
    },
  }));

  const primary = () => {
    if (!toResults) return tab === 'keywords' ? 'Search' : 'Set';
    return readOnly ? 'Edit' : 'Save';
  };
  const confirm = () => {
    if (!toResults) {
      if (tab === 'keywords') return search();
      return onSave({
        keywords,
        officialOnly,
        postIds: [],
        urls: urls
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean),
      });
    }
    if (readOnly) {
      // `Edit` reopens the keywords with the saved campaign still in them.
      setReadOnly(false);
      setStep('input');
      setTab('keywords');
      return;
    }
    onSave({ keywords, officialOnly, postIds: kept.map((post) => post.id), urls: undefined });
  };
  const confirmDisabled = () => {
    if (pending || searching) return true;
    if (!toResults) return tab === 'keywords' ? keywords.length === 0 : urls.trim().length === 0;
    return !readOnly && kept.length === 0;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Configure campaigns"
      description="Set up campaign keywords and phrases to track audience response and campaign performance."
      icon={<Icon name="campaign" />}
      fullWidth
      scrollBody={toResults}
      actionsLayout="end"
      actionsLeading={
        toResults ? (
          <Button
            hierarchy="ghostColor"
            disabled={readOnly}
            onClick={() => {
              setStep('input');
              setDropped([]);
            }}
          >
            Reset search
          </Button>
        ) : tab === 'keywords' ? (
          <Toggle
            label="Official page posts only"
            checked={officialOnly}
            onChange={setOfficialOnly}
          />
        ) : undefined
      }
      confirmLabel={searching ? 'Searching…' : pending ? 'Saving…' : primary()}
      confirmDisabled={confirmDisabled()}
      onConfirm={confirm}
      cancelLabel="Cancel"
    >
      {toResults ? (
        <Table
          label="Detected posts"
          type={shown.length === 0 ? 'empty' : 'default'}
          columns={columns}
          rows={rows}
          emptyLabel={
            searching ? 'Searching…' : 'No posts match those keywords. Try resetting the search.'
          }
          header={
            <TableHeader
              title="Content"
              badge={`${shown.length} post${shown.length === 1 ? '' : 's'} ${
                readOnly ? 'configured' : 'detected'
              }`}
              helpText="Every post these campaign keywords found. Untick one to leave it out."
            />
          }
        />
      ) : (
        <div className={styles.campaign}>
          {/* `size` is left at `md`: Figma's bar is 28 tall (2 + 24 + 2), which
              is md — sm is 20. */}
          <Tabs
            label="How to add campaign posts"
            value={tab}
            onChange={(value) => setTab(value as 'keywords' | 'manual')}
            items={[
              { value: 'keywords', label: 'Add by keywords' },
              { value: 'manual', label: 'Add manually' },
            ]}
          />
          {tab === 'keywords' ? (
            <TextareaField
              type="tags"
              className={styles.campaignField}
              aria-label="Campaign keywords"
              placeholder="Separate keywords by pressing ‘tab’…"
              value={draft}
              onValueChange={setDraft}
              tags={keywords}
              onTagsChange={setKeywords}
            />
          ) : (
            <TextareaField
              className={styles.campaignField}
              aria-label="Campaign post URLs"
              placeholder="Paste one URL per line"
              value={urls}
              onValueChange={setUrls}
            />
          )}
        </div>
      )}
    </Modal>
  );
}

export function ShareReportModal({
  open,
  reportName,
  onClose,
}: {
  open: boolean;
  reportName: string;
  onClose: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('view');
  const [notify, setNotify] = useState(true);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Share report"
      description={`Invite a teammate to “${reportName}”.`}
      icon={<Icon name="share" />}
      confirmLabel="Send invitation"
      confirmDisabled={!email.trim()}
      onConfirm={onClose}
      cancelLabel="Cancel"
    >
      <div className={styles.form}>
        <InputField
          label="Email address"
          value={email}
          onValueChange={setEmail}
          inputMode="email"
          placeholder="name@company.com"
        />
        <Select
          label="Permission"
          value={role}
          onValueChange={setRole}
          options={[
            { value: 'view', label: 'Can view' },
            { value: 'edit', label: 'Can edit' },
          ]}
        />
        <Checkbox label="Notify by email" checked={notify} onChange={setNotify} />
      </div>
    </Modal>
  );
}
